import { create } from 'zustand';

// 10.4 后台朗读（桌面壳）：
// Web Speech API（speechSynthesis）在 Tauri WebView 中不受页面 visibility 状态影响，
// 窗口最小化/失焦时朗读会继续运行（不同于浏览器标签页会被节流/暂停）。
// 因此本 store 刻意不监听 `visibilitychange`，也不在失焦时暂停朗读。
// 桌面壳（apps/desktop）依赖这一行为实现后台朗读；浏览器端同样可用。
// 若日后要在纯浏览器标签页失焦时暂停，请单独在此处按 `navigator.userAgent` 判断后再加监听。

interface TtsState {
  playing: boolean;
  currentBlockIndex: number;
  rate: number;
  voices: SpeechSynthesisVoice[];
  selectedVoiceURI: string | null;

  initVoices: () => void;
  play: (texts: string[], startIndex?: number) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  next: (texts: string[]) => void;
  setRate: (rate: number) => void;
  setVoice: (uri: string) => void;
}

export const useTtsStore = create<TtsState>((set, get) => ({
  playing: false,
  currentBlockIndex: -1,
  rate: 1.0,
  voices: [],
  selectedVoiceURI: null,

  initVoices: () => {
    if (!('speechSynthesis' in window)) return;
    const load = () => { set({ voices: window.speechSynthesis.getVoices() }); };
    load();
    window.speechSynthesis.onvoiceschanged = load;
  },

  play: (texts, startIndex = 0) => {
    if (!('speechSynthesis' in window) || texts.length === 0) return;
    window.speechSynthesis.cancel();

    const state = get();
    const utterance = new SpeechSynthesisUtterance(texts[startIndex]);
    utterance.rate = state.rate;
    if (state.selectedVoiceURI) {
      const voice = state.voices.find((v) => v.voiceURI === state.selectedVoiceURI);
      if (voice) utterance.voice = voice;
    }

    utterance.onboundary = () => {
      // 10.8 进度跟随朗读：高亮当前 block + 自动滚动
      const idx = get().currentBlockIndex;
      const spoken = texts[idx];
      if (!spoken) return;
      const trimmed = spoken.trim();
      if (!trimmed) return;
      // 找到当前正在朗读的 block 元素（textContent 包含该段原文）
      const blocks = Array.from(document.querySelectorAll('[data-block-id]'));
      const active: Element | null = blocks.find((el) => el.textContent?.includes(trimmed)) ?? null;
      if (active && !active.classList.contains('tts-active')) {
        // 移除上一个 block 的 class
        document.querySelectorAll('.tts-active').forEach((e) => e.classList.remove('tts-active'));
        active.classList.add('tts-active');
        // 自动滚动到当前 block
        active.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    utterance.onend = () => {
      const nextIdx = startIndex + 1;
      if (nextIdx < texts.length) {
        set({ currentBlockIndex: nextIdx });
        get().play(texts, nextIdx);
      } else {
        // 10.8 朗读结束：清除高亮
        document.querySelectorAll('.tts-active').forEach((e) => e.classList.remove('tts-active'));
        set({ playing: false, currentBlockIndex: -1 });
      }
    };

    set({ playing: true, currentBlockIndex: startIndex });
    // 10.9 持久化当前位置
    localStorage.setItem('hump-tts-position', String(startIndex));
    window.speechSynthesis.speak(utterance);
  },

  pause: () => { window.speechSynthesis?.pause(); set({ playing: false }); },
  resume: () => { window.speechSynthesis?.resume(); set({ playing: true }); },
  stop: () => {
    window.speechSynthesis?.cancel();
    // 10.8 停止时清除所有 tts-active 高亮
    document.querySelectorAll('.tts-active').forEach((e) => e.classList.remove('tts-active'));
    // 10.9 断点续听：停止时记住位置（不清 -1）
    const { currentBlockIndex } = get();
    if (currentBlockIndex >= 0) {
      localStorage.setItem('hump-tts-position', String(currentBlockIndex));
    }
    set({ playing: false });
  },
  next: (texts) => {
    const nextIdx = get().currentBlockIndex + 1;
    if (nextIdx < texts.length) get().play(texts, nextIdx);
  },
  setRate: (rate) => set({ rate }),
  setVoice: (uri) => set({ selectedVoiceURI: uri }),
  /** 10.9 获取上次朗读位置 */
  getLastPosition: (): number => {
    const saved = localStorage.getItem('hump-tts-position');
    return saved ? parseInt(saved, 10) : -1;
  },
  /** 10.9 清除位置 */
  clearLastPosition: (): void => {
    localStorage.removeItem('hump-tts-position');
  },
}));
